/**
 * Per-game configuration. Plain JSON data — each game declares its own in its
 * `main.ts` and hands it to the `MagicSlot` constructor, which registers it
 * here before any controller is built.
 */
import type { RulesPopupOptions } from 'controllers/RulesPopup.controller';

export type GameConfig = {
    cols: number;
    rows: number;
    /**
     * The game's regular, paying symbols — the pool a reel fills from while it spins
     * and for the hidden buffer cells. Only these come up at random.
     */
    symbols: string[];
    /**
     * The symbols this game gives its own meaning to: wild, scatter, bonus. They are
     * kept out of every random fill, so a spinning reel can never tease a scatter the
     * round did not deal — they only reach the reels from a result board.
     */
    specialSymbols: string[];
    /** Minimum time (ms) the reels keep spinning, even if the result arrives sooner. */
    minSpinTime: number;
    /** Delay (ms) between starting consecutive reels spinning. 0 starts the whole board at once. */
    reelStartDelay: number;
    /** Delay (ms) between releasing consecutive reels to stop. */
    reelStopDelay: number;
    /** Extra time (ms) a backend-flagged reel keeps spinning with its anticipation overlay showing. */
    anticipationSpinTime: number;
    /** Below these viewport sizes (px) the game scales down to fit. */
    minHeight: number;
    minWidth: number;
    sounds: string[];
    /**
     * The rules page the UI's info button opens, rendered from a Markdown file into the popup
     * skeleton. Omit it and the info button stays unwired — a game without a `rules.md` should
     * not be fetching one.
     */
    rules?: RulesPopupOptions;
};

let gameConfig: GameConfig | null = null;

/** Registers the running game's config. Called by the `MagicSlot` constructor. */
export function setGameConfig(config: GameConfig) {
    gameConfig = config;
}

export function getGameConfig(): GameConfig {
    if (!gameConfig) {
        throw new Error('Game config is not set — pass it to the MagicSlot constructor.');
    }

    return gameConfig;
}
